// Purchasing zod DTOs (S7) — BUILD_SPEC.md §5.4. Request/response schemas for
// suppliers, supplier_contracts and purchase_orders (+lines), consumed by
// services/api/src/modules/purchasing via `@hono/zod-validator` and, later, by the
// web app for end-to-end types (z.infer).
//
// Cross-field pricing-basis validation lives HERE (not in packages/db, which has no
// I/O and no business-rule layer, and not in a shared file, since it is specific to
// this module's two "basis + bands/price" shapes: supplier_contracts and
// purchase_order_lines both carry the same trio of fields with the same rule).
import { z } from 'zod';
import { moneyCentsSchema, quantitySchema, ulidSchema } from './common.js';
import { PRICING_BASES, PURCHASE_ORDER_STATUSES, VAT_TREATMENTS } from '../enums.js';
// ---------------------------------------------------------------------------
// Shared: moisture bands + basis/price/bands cross-field validation
// ---------------------------------------------------------------------------
export const moistureBandSchema = z
    .object({
    mcMin: z.number().min(0).max(100),
    mcMax: z.number().min(0).max(100),
    priceCents: moneyCentsSchema,
})
    .refine((b) => b.mcMax > b.mcMin, { message: 'mcMax must be greater than mcMin', path: ['mcMax'] });
/**
 * Shared cross-field rule for every {pricingBasis, priceCentsPerTonne, bands} triple
 * (supplier_contracts and purchase_order_lines both carry one): FLAT_WET/BDT require
 * a flat price and no bands; MOISTURE_BAND requires a non-empty, CONTIGUOUS,
 * NON-OVERLAPPING band list (plan.md G6 acceptance) and no flat price. Attached via
 * `.superRefine` on the containing object schema — runs on the already-parsed value,
 * so `bands[].priceCents` here is a `bigint` (moneyCentsSchema's transform already
 * applied), not the raw wire string/bigint union.
 */
function checkPricingBasis(val, ctx) {
    if (val.pricingBasis === 'MOISTURE_BAND') {
        if (val.priceCentsPerTonne != null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'priceCentsPerTonne must not be set for MOISTURE_BAND pricing (use bands)',
                path: ['priceCentsPerTonne'],
            });
        }
        if (!val.bands || val.bands.length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'bands are required for MOISTURE_BAND pricing',
                path: ['bands'],
            });
            return;
        }
        const sorted = [...val.bands].sort((a, b) => a.mcMin - b.mcMin);
        for (let i = 1; i < sorted.length; i++) {
            const prev = sorted[i - 1];
            const curr = sorted[i];
            if (curr.mcMin < prev.mcMax) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `bands overlap: [${prev.mcMin},${prev.mcMax}] and [${curr.mcMin},${curr.mcMax}]`,
                    path: ['bands'],
                });
            }
            else if (curr.mcMin > prev.mcMax) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: `bands must be contiguous: gap between [${prev.mcMin},${prev.mcMax}] and [${curr.mcMin},${curr.mcMax}]`,
                    path: ['bands'],
                });
            }
        }
    }
    else {
        // FLAT_WET | BDT
        if (val.bands != null && val.bands.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `bands must not be set for ${val.pricingBasis} pricing (use priceCentsPerTonne)`,
                path: ['bands'],
            });
        }
        if (val.priceCentsPerTonne == null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `priceCentsPerTonne is required for ${val.pricingBasis} pricing`,
                path: ['priceCentsPerTonne'],
            });
        }
    }
}
const isActiveQueryParamSchema = z
    .enum(['true', 'false'])
    .optional()
    .transform((v) => (v === undefined ? undefined : v === 'true'));
/** Query-string pagination (cursor + coerced numeric limit) shared by every list
 * endpoint in this module. `paginationRequestSchema` (schemas/common.ts) models the
 * JSON-body shape (`limit` as a plain number, max 200, default 50); HTTP query
 * params arrive as strings, so `limit` needs `z.coerce` here — same cap/default,
 * kept local to this module rather than adding a second shape to common.ts. */
const listQueryBaseSchema = z.object({
    cursor: z.string().optional(),
    limit: z.coerce.number().int().positive().max(200).default(50),
});
// ---------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------
export const supplierCreateSchema = z.object({
    code: z.string().min(1).max(64),
    name: z.string().min(1).max(200),
    vatNumber: z.string().max(64).nullable().optional(),
    email: z.string().email().nullable().optional(),
    phone: z.string().max(64).nullable().optional(),
    paymentTerms: z.string().max(200).nullable().optional(),
    xeroContactId: z.string().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
});
export const supplierUpdateSchema = z.object({
    code: z.string().min(1).max(64).optional(),
    name: z.string().min(1).max(200).optional(),
    vatNumber: z.string().max(64).nullable().optional(),
    email: z.string().email().nullable().optional(),
    phone: z.string().max(64).nullable().optional(),
    paymentTerms: z.string().max(200).nullable().optional(),
    xeroContactId: z.string().max(200).nullable().optional(),
    isActive: z.boolean().optional(),
});
export const supplierListQuerySchema = listQueryBaseSchema.extend({
    isActive: isActiveQueryParamSchema,
});
// ---------------------------------------------------------------------------
// Supplier contracts
// ---------------------------------------------------------------------------
const supplierContractBaseSchema = z.object({
    supplierId: ulidSchema,
    productId: ulidSchema,
    pricingBasis: z.enum(PRICING_BASES),
    priceCentsPerTonne: moneyCentsSchema.nullable().optional(),
    bands: z.array(moistureBandSchema).nullable().optional(),
    validFrom: z.coerce.date(),
    validTo: z.coerce.date().nullable().optional(),
    isActive: z.boolean().optional(),
});
export const supplierContractCreateSchema = supplierContractBaseSchema.superRefine(checkPricingBasis);
const supplierContractUpdateBaseSchema = z.object({
    supplierId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
    pricingBasis: z.enum(PRICING_BASES).optional(),
    priceCentsPerTonne: moneyCentsSchema.nullable().optional(),
    bands: z.array(moistureBandSchema).nullable().optional(),
    validFrom: z.coerce.date().optional(),
    validTo: z.coerce.date().nullable().optional(),
    isActive: z.boolean().optional(),
});
// The basis/price/bands rule only makes sense to re-check when a caller actually
// touches one of that trio on an update (a caller changing only `isActive`, say,
// shouldn't be forced to resend a valid pricingBasis+bands combination it never
// asked to change).
export const supplierContractUpdateSchema = supplierContractUpdateBaseSchema.superRefine((val, ctx) => {
    if (val.pricingBasis === undefined && val.priceCentsPerTonne === undefined && val.bands === undefined)
        return;
    if (val.pricingBasis === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'pricingBasis is required when changing priceCentsPerTonne or bands',
            path: ['pricingBasis'],
        });
        return;
    }
    checkPricingBasis({ pricingBasis: val.pricingBasis, priceCentsPerTonne: val.priceCentsPerTonne, bands: val.bands }, ctx);
});
export const supplierContractListQuerySchema = listQueryBaseSchema.extend({
    supplierId: ulidSchema.optional(),
    productId: ulidSchema.optional(),
    isActive: isActiveQueryParamSchema,
});
// ---------------------------------------------------------------------------
// Purchase orders + lines
// ---------------------------------------------------------------------------
const qtyTonnesSchema = quantitySchema.refine((v) => Number(v) > 0, { message: 'qtyTonnes must be positive' });
/**
 * Mass-priced (tonnes) purchase order line — the original, still-default shape.
 * `.strict()` so a line can never carry BOTH this shape's fields and the EACH
 * (`countLineSchema`) shape's fields at once; `purchaseOrderLineInputSchema`
 * below relies on that to reject a mixed-mode line (packages/db/src/schema/
 * purchasing.ts's `purchase_order_lines_quantity_mode` CHECK mirrors this at
 * the DB layer).
 */
const purchaseOrderLineBaseSchema = z
    .object({
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    qtyTonnes: qtyTonnesSchema,
    pricingBasis: z.enum(PRICING_BASES),
    priceCentsPerTonne: moneyCentsSchema.nullable().optional(),
    bands: z.array(moistureBandSchema).nullable().optional(),
    vatTreatment: z.enum(VAT_TREATMENTS),
    costCentreId: ulidSchema.nullable().optional(),
})
    .strict();
const massLineSchema = purchaseOrderLineBaseSchema.superRefine(checkPricingBasis);
/** Quantity of a countable (EACH base UOM) purchase — Howick packaging/cartons/
 * bags bought by count rather than mass. Same 3dp-max decimal-string idiom as
 * every other quantity in this codebase (`quantitySchema`, common.ts), but
 * additionally WHOLE units only (fix round 2026-07-07): a fractional count
 * ("2.5 boxes") would invent sub-cent money at the `eachPayableCents` half-up
 * rounding boundary. A whole value written with decimals ('2.000') still passes
 * — matching the DB CHECK `qty = trunc(qty)` (packages/db/src/schema/
 * purchasing.ts, `purchase_order_lines_qty_integer`). */
const qtyEachSchema = quantitySchema
    .refine((v) => Number(v) > 0, { message: 'qty must be positive' })
    .refine((v) => Number.isInteger(Number(v)), { message: 'qty must be a whole number of units' });
/**
 * EACH-priced (count) purchase order line — packaging/consumables bought and
 * priced per unit rather than per tonne. Mirrors the mass line's optional
 * fields (vatTreatment, costCentreId) but carries none of the moisture-pricing
 * machinery (pricingBasis/bands) since a count line is never moisture-priced.
 * `.strict()` — see purchaseOrderLineBaseSchema's doc comment.
 */
export const countLineSchema = z
    .object({
    lineNo: z.number().int().positive(),
    productId: ulidSchema,
    qty: qtyEachSchema,
    priceCentsPerUnit: moneyCentsSchema,
    vatTreatment: z.enum(VAT_TREATMENTS),
    costCentreId: ulidSchema.nullable().optional(),
})
    .strict();
/**
 * A purchase order line is EITHER mass-priced (qtyTonnes+pricingBasis, the
 * original shape) OR EACH-priced (qty+priceCentsPerUnit, `countLineSchema`) —
 * never both, never neither. `.strict()` on both branches makes a line that
 * mixes fields from each shape fail BOTH branches (extra keys are rejected,
 * not silently stripped), so `z.union` here doubles as the cross-mode guard;
 * no separate discriminant/tag field exists on the wire.
 */
export const purchaseOrderLineInputSchema = z.union([massLineSchema, countLineSchema]);
export const purchaseOrderCreateSchema = z.object({
    siteId: ulidSchema,
    supplierId: ulidSchema,
    expectedAt: z.coerce.date().nullable().optional(),
    deliveryLocationId: ulidSchema.nullable().optional(),
    notes: z.string().nullable().optional(),
    currency: z.string().length(3).optional(),
    lines: z.array(purchaseOrderLineInputSchema).min(1, 'at least one line is required'),
});
export const purchaseOrderUpdateSchema = z.object({
    siteId: ulidSchema.optional(),
    supplierId: ulidSchema.optional(),
    expectedAt: z.coerce.date().nullable().optional(),
    deliveryLocationId: ulidSchema.nullable().optional(),
    notes: z.string().nullable().optional(),
    currency: z.string().length(3).optional(),
    lines: z.array(purchaseOrderLineInputSchema).min(1, 'at least one line is required').optional(),
});
export const purchaseOrderListQuerySchema = listQueryBaseSchema.extend({
    status: z.enum(PURCHASE_ORDER_STATUSES).optional(),
    supplierId: ulidSchema.optional(),
});
//# sourceMappingURL=purchasing.js.map